import logger from '../../Utils/logger';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './Style.module.css';
import { Counter } from '../Counter/Counter';
import { removeHtmlTag } from '../../Utils/Helper';
import { Snakbar } from '../Snakbar/Snakbar';
import { useWebSettings } from '../../Context/WebSettings/WebSettingsContext';
import { BtnLink } from '../Buttons/BtnLink';
import { VoucherTermsPopup } from '../Popup/VoucherTermsPopup/VoucherTermsPopup';

export const Service = ({
  id: globalid,
  title,
  description = "",
  image,
  services = [],
  updateMemberList = () => {},
  totalSelectedCount,
  serviceList = [],
  setServiceList = () => {},
  members = [],
  selectedMember = null,
  /** Single (1) and group (2) booking: services live on `members`; use member counters & updateMemberList only. */
  memberServiceFlow = false,
  isVoucher = false,
}) => {
  const { maxServiceCount } = useWebSettings();
  const applyMemberUpdate = memberServiceFlow && updateMemberList ? updateMemberList : updateMemberList;

  const [getMaxServices, setGetMaxServices] = useState(false);
  const [perMemberSirviceCountLocal, setPerMemberSirviceCountLocal] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [imgError, setImgError] = useState(false);
  const [showVoucherTermsPopup, setShowVoucherTermsPopup] = useState(false);

  const memberSlotCountForId = useCallback(
    (pkgId) => {
      if (!memberServiceFlow || !Array.isArray(members) || !Number.isInteger(selectedMember)) {
        return null;
      }
      return (members[selectedMember]?.services ?? []).filter((s) => s.id === pkgId).length;
    },
    [memberServiceFlow, members, selectedMember]
  );

  const computedTotalSelectedCount = useMemo(() => {
    if (!Array.isArray(serviceList)) return 0;

    if (memberServiceFlow && Array.isArray(members) && Number.isInteger(selectedMember)) {
      return (members[selectedMember]?.services ?? []).length;
    }
    return serviceList.reduce((acc, item) => {
      if (!Array.isArray(item.services)) return acc;
      return acc + item.services.reduce((sum, subitem) => sum + (subitem.count || 0), 0);
    }, 0);
  }, [serviceList, memberServiceFlow, members, selectedMember]);

  useEffect(() => {
    setPerMemberSirviceCountLocal(computedTotalSelectedCount);
  }, [computedTotalSelectedCount]);

  const isDisable = computedTotalSelectedCount >= maxServiceCount;

  // Check if any non-15-min service is selected
  const hasNonFifteenMinService = useMemo(() => {
    if (memberServiceFlow && Array.isArray(members) && Number.isInteger(selectedMember)) {
      const memberServices = members[selectedMember]?.services ?? [];
      return memberServices.some((s) => s.duration && Number(s.duration) > 15);
    }
    if (!Array.isArray(serviceList)) return false;
    return serviceList.some((category) =>
      Array.isArray(category.services) &&
      category.services.some((service) => service.count > 0 && service.duration && Number(service.duration) > 15)
    );
  }, [serviceList, memberServiceFlow, members, selectedMember]);

  // Check if a service is 15-minute service
  const isFifteenMinService = useCallback((pkg) => pkg.duration && Number(pkg.duration) === 15, []);

  // Determine if 15-min service should be disabled (no other service selected yet)
  const shouldDisable15MinService = useCallback(
    (pkg) => isFifteenMinService(pkg) && !hasNonFifteenMinService,
    [isFifteenMinService, hasNonFifteenMinService]
  );

  const showCounter = useCallback(
    (id, selectedPkg) => {
      try {
        setServiceList((prevList) =>
          prevList.map((pkg) => ({
            ...pkg,
            services: Array.isArray(pkg.services)
              ? pkg.services.map((pkgItem) =>
                  pkgItem.id === id ? { ...pkgItem, count: 1, isCounterVisible: true } : pkgItem
                )
              : [],
          }))
        );

        const line = { ...selectedPkg, count: 1, isCounterVisible: true };
        applyMemberUpdate(globalid, line);
      } catch (error) {
        logger.error('Error inside showCounter:', error);
      }
    },
    [applyMemberUpdate, globalid, setServiceList]
  );

  const incrementCounter = useCallback(
    (id, selectedPkg) => {
      if (selectedPkg.count < maxServiceCount) {
        setServiceList((prevList) =>
          prevList.map((pkg) => ({
            ...pkg,
            services: Array.isArray(pkg.services)
              ? pkg.services.map((pkgItem) =>
                  pkgItem.id === id && selectedPkg.count < maxServiceCount
                    ? { ...pkgItem, count: selectedPkg.count + 1, isCounterVisible: true }
                    : pkgItem
                )
              : [],
          }))
        );

        const updatedPkg = { ...selectedPkg, count: selectedPkg.count + 1, isCounterVisible: true };
        applyMemberUpdate(globalid, updatedPkg);
      }
    },
    [applyMemberUpdate, globalid, maxServiceCount, setServiceList]
  );

  const decrementCounter = useCallback(
    (id, selectedPkg) => {
      if (selectedPkg.count > 0) {
        setServiceList((prevList) =>
          prevList.map((pkg) => ({
            ...pkg,
            services: Array.isArray(pkg.services)
              ? pkg.services.map((pkgItem) =>
                  pkgItem.id === id
                    ? {
                        ...pkgItem,
                        count: selectedPkg.count - 1,
                        isCounterVisible: selectedPkg.count - 1 > 0,
                      }
                    : pkgItem
                )
              : [],
          }))
        );

        const updatedPkg = {
          ...selectedPkg,
          count: selectedPkg.count - 1,
          isCounterVisible: selectedPkg.count - 1 > 0,
        };
        if (memberServiceFlow) {
          applyMemberUpdate(globalid, updatedPkg, true);
        }
      }
    },
    [applyMemberUpdate, globalid, memberServiceFlow, setServiceList]
  );



  useEffect(() => {
    if (getMaxServices) {
      setShowSnackbar(true);
      const hideTimeout = setTimeout(() => {
        setShowSnackbar(false);
        const cleanupTimeout = setTimeout(() => setGetMaxServices(false), 300);
        return () => clearTimeout(cleanupTimeout);
      }, 3000);
  
      return () => clearTimeout(hideTimeout);
    }
  }, [getMaxServices]);
  

  
  const renderedServices = useMemo(() => {
    if (services.length === 0) {
      return <div className={styles.notFound}>No service is available</div>;
    }
    return services.map((pkg, index) => {
      const slots = memberSlotCountForId(pkg.id);
      const displayCount = slots != null ? slots : pkg?.count ?? 0;
      const pkgForCounter = { ...pkg, count: displayCount };
      const counterVisible = slots != null ? displayCount > 0 : pkg?.isCounterVisible;
      const memberPrice = pkg?.price_rate?.member_after_6_or_weekends ?? 0;
      const haveSlots = pkg?.is_service_available ? true : false;
      const nonMemberPrice = pkg?.price_rate?.nonMemberPrice;
      return (
        <React.Fragment key={pkg.id}>
          <div className={`${styles.package} ${!haveSlots ? styles.greyedOut : ''}`}>
            <div className={styles.left}>
              <h6>{pkg?.duration} Mins {pkg?.name}</h6>
               <div className={styles.packageDetail}>
                 <div className={`${styles.startingPrice}`}>
                  <span>Member</span> S${memberPrice}+
                </div>
                <div className={styles.duration}><span>Non-Member</span> S${nonMemberPrice}+</div>
              </div>
            </div>
            <div className={styles.right}>
              {!haveSlots ? (
                <span className={styles.slotsFullBadge}>Online Slots Full</span>
              ) : (
                <Counter
                  id={pkg?.id}
                  price={pkg?.price}
                  isCounterVisible={counterVisible}
                  disabled={isDisable || shouldDisable15MinService(pkg)}
                  count={displayCount}
                  customeClass={shouldDisable15MinService(pkg) ? styles.greyedOut : ''}
                  handleShowCounter={() => {
                    if (shouldDisable15MinService(pkg)) {
                      setSnackbarMessage('Please select another service first');
                      setShowSnackbar(true);
                      setTimeout(() => setShowSnackbar(false), 3000);
                      return;
                    }
                    if (isDisable || pkg.disabledBtn) {
                      setGetMaxServices(true);
                    } else {
                      showCounter(pkg?.id, pkgForCounter);
                    }
                  }}
                  handleIncrement={() => {
                    if (shouldDisable15MinService(pkg)) {
                      setSnackbarMessage('Please select another service first');
                      setShowSnackbar(true);
                      setTimeout(() => setShowSnackbar(false), 3000);
                      return;
                    }
                    if (isDisable || pkg.disabledBtn) {
                      setGetMaxServices(true);
                    } else {
                      incrementCounter(pkg?.id, pkgForCounter);
                    }
                  }}
                  handleDecrement={() => decrementCounter(pkg?.id, pkgForCounter)}
                  disabledBtn={pkg.disabledBtn ?? false}
                />
              )}
            </div>
          </div>
          {index < services.length - 1 && <hr className="line" />}
        </React.Fragment>
      );
    });
  }, [
    services,
    isDisable,
    memberSlotCountForId,
    showCounter,
    incrementCounter,
    decrementCounter,
    shouldDisable15MinService,
  ]);



  useEffect(() => {
    const body = document.body;
    body.classList.remove('overlay', 'half', 'overlay-index', 'overflow-hidden');
    if (showVoucherTermsPopup) {
      body.classList.add('overlay', 'overflow-hidden');
    }
  }, [showVoucherTermsPopup]);


  return (
    <div className={styles.serviceCardOuter}>
    {(getMaxServices || snackbarMessage) && (
    <Snakbar
      text={snackbarMessage || `You can add up to ${maxServiceCount} services per pax.`}
      active={showSnackbar}
      varient="danger"
    />
    )}

    <div className={`${styles.serviceCard} ${services.length > 0 ? "" : styles.disabled}`}>
      <div className={styles.serviceTitleCard}>
        <h2 className={styles.title}>{title}</h2>
        {!isVoucher ? (
          typeof description === "string" && description.trim() && <div className={styles.description}>{removeHtmlTag(description)}</div>
        ) : (
          <>
          <div className={styles.description}>Can be used to redeem for all services at all participating outlets.</div>
          <div className={styles.description}>
            <BtnLink 
            label="Read Terms and Conditions."
            handleClick={() => setShowVoucherTermsPopup(true)}
            title="Read Terms and Conditions"
            customClass={styles.termsLink}
            />
          </div>
          </>
        ) }
        
      </div>
      {image && !imgError && (
        <figure style={{ cursor: "default" }}>
          <img
            src={image}
            alt={title}
            className="object-cover"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        </figure>
      )}
      <div className={styles.servicePackageCard}>{renderedServices}</div>
    </div>
    <VoucherTermsPopup
    active={showVoucherTermsPopup}
    onClose={() => setShowVoucherTermsPopup(false)}
    />
    </div>
  );
};
